import React from 'react';
import { Backdrop, Box, Paper, Typography, CircularProgress } from '@mui/material';

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  message = "Loading..."
}) => {
  return (
    <Backdrop
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backdropFilter: 'blur(4px)'
      }}
      open={isVisible}
    >
      <Paper
        elevation={24}
        sx={{
          borderRadius: 4,
          p: { xs: 3, sm: 4 },
          maxWidth: { xs: 300, sm: 400 },
          minWidth: { xs: 280, sm: 400 },
          mx: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: 'background.paper'
        }}
      >
        {/* Custom Gradient Spinner */}
        <Box sx={{ position: 'relative', mb: { xs: 2, sm: 3 } }}>
          <CircularProgress
            size={64}
            thickness={3}
            sx={{
              color: 'transparent',
              width: { xs: '48px !important', sm: '64px !important' },
              height: { xs: '48px !important', sm: '64px !important' },
              '& .MuiCircularProgress-circle': {
                stroke: 'url(#gradient-spinner)',
              },
            }}
          />
          <svg width="0" height="0">
            <defs>
              <linearGradient id="gradient-spinner" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F69A7E" />
                <stop offset="100%" stopColor="#939BEC" />
              </linearGradient>
            </defs>
          </svg>
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            mb: 1.5,
            textAlign: 'center',
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}
        >
          {message}
        </Typography>

        {/* Animated dots */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                width: { xs: 6, sm: 8 },
                height: { xs: 6, sm: 8 },
                borderRadius: '50%',
                bgcolor: index % 2 === 0 ? '#F69A7E' : '#939BEC',
                animation: 'bounce 1s infinite',
                animationDelay: `${index * 150}ms`,
                '@keyframes bounce': {
                  '0%, 20%, 53%, 80%, 100%': {
                    transform: 'translateY(0px)',
                  },
                  '40%, 43%': {
                    transform: 'translateY(-8px)',
                  },
                  '70%': {
                    transform: 'translateY(-4px)',
                  },
                  '90%': {
                    transform: 'translateY(-2px)',
                  }
                }
              }}
            />
          ))}
        </Box>
      </Paper>
    </Backdrop>
  );
};

export default LoadingOverlay;
